import { useCallback, useRef, useState } from 'react';
import type { ActivityIcon, ActivityPhase, ActivityStep, CoTStepData } from '../components/app/cot/types';
import type { CoTAction } from '../components/app/cot/CoTAnswerBlock';
import type { ChefPlan, CoTErrorCode, StepCategory } from '../../../shared/types';
import type { PermissionTier } from '../../../shared/types';
import type { EditBatch } from '@corner/shared';

// ─── Types ───────────────────────────────────────

export type CoTStatus = 'idle' | 'planning' | 'working' | 'done' | 'error';

export interface CoTAnswer {
  content: string;
  actions?: CoTAction[];
}

export interface CoTState {
  status: CoTStatus;
  steps: CoTStepData[];
  activityPhases: ActivityPhase[];
  understanding?: string;
  answer?: CoTAnswer;
  /** ID of the message in the conversation that corresponds to the CoT answer.
   *  Used by ChatPanel to hide the MessageBubble when CoTContainer is showing. */
  answerMessageId?: string;
  /** Number of errors in this session (resets on reset()) */
  failureCount: number;
  /** True when the run produced a result but some steps failed */
  partialCompletion: boolean;
}

const IDLE_STATE: CoTState = { status: 'idle', steps: [], activityPhases: [], failureCount: 0, partialCompletion: false };

function makeId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function activityIconForCategory(category?: StepCategory): ActivityIcon | undefined {
  switch (category) {
    case 'file': return 'file';
    case 'search': return 'search';
    case 'edit': return 'edit';
    case 'web': return 'web';
    case 'draft': return 'draft';
    case 'sign': return 'check';
    case 'check': return 'check';
    case 'flag': return 'flag';
    default: return undefined;
  }
}

// ─── Hook ────────────────────────────────────────

export default function useCoTState() {
  const [state, setState] = useState<CoTState>(IDLE_STATE);
  // Keep a mutable ref so SSE callbacks always see latest steps without stale closures
  const stepsRef = useRef<CoTStepData[]>([]);
  const activityRef = useRef<ActivityPhase[]>([]);
  const failureCountRef = useRef(0);
  /** Sous Chef streams one SSE event per model token — flushing setState each time freezes the tab. */
  const pendingThinkingRef = useRef('');
  const thinkingRafRef = useRef<number | null>(null);

  const syncActivity = useCallback((next: ActivityPhase[]) => {
    activityRef.current = next;
    setState((s) => ({ ...s, activityPhases: next }));
  }, []);

  const ensurePhase = useCallback((): ActivityPhase => {
    const phases = activityRef.current;
    const current = phases[phases.length - 1];
    if (current && !current.collapsed) return current;
    const next: ActivityPhase = { id: makeId('phase'), steps: [], collapsed: false };
    syncActivity([...phases, next]);
    return next;
  }, [syncActivity]);

  const patchCurrentPhase = useCallback((patcher: (phase: ActivityPhase) => ActivityPhase) => {
    const phases = activityRef.current;
    const phase = phases[phases.length - 1] ?? { id: makeId('phase'), steps: [], collapsed: false };
    const base = phases.length > 0 ? phases.slice(0, -1) : [];
    syncActivity([...base, patcher(phase)]);
  }, [syncActivity]);

  const settleActiveActivity = useCallback((status: 'success' | 'error' = 'success') => {
    patchCurrentPhase((phase) => ({
      ...phase,
      steps: phase.steps.map((step, index) => {
        if (index !== phase.steps.length - 1 || step.status !== 'active') return step;
        if (step.kind === 'thinking') return { ...step, status, expanded: true };
        return { ...step, status };
      }),
    }));
  }, [patchCurrentPhase]);

  const cancelThinkingFlush = useCallback(() => {
    if (thinkingRafRef.current != null) {
      cancelAnimationFrame(thinkingRafRef.current);
      thinkingRafRef.current = null;
    }
  }, []);

  const applyThinkingTextBatch = useCallback((batch: string) => {
    if (!batch) return;
    ensurePhase();
    patchCurrentPhase((phase) => {
      const steps = [...phase.steps];
      const last = steps[steps.length - 1];
      if (!last || last.kind !== 'thinking' || last.status !== 'active') {
        steps.push({
          id: makeId('thinking'),
          kind: 'thinking',
          label: 'Reasoning',
          body: batch,
          status: 'active',
          expanded: true,
        });
      } else {
        steps[steps.length - 1] = { ...last, body: `${last.body}${batch}` };
      }
      return { ...phase, steps, collapsed: false };
    });
    setState((s) => (s.status === 'idle' ? { ...s, status: 'planning' } : s));
  }, [ensurePhase, patchCurrentPhase]);

  /** Apply any tokens still buffered before a phase transition (so text is not lost). */
  const drainPendingThinkingChunks = useCallback(() => {
    cancelThinkingFlush();
    const batch = pendingThinkingRef.current;
    pendingThinkingRef.current = '';
    if (batch) applyThinkingTextBatch(batch);
  }, [applyThinkingTextBatch, cancelThinkingFlush]);

  const onPlanning = useCallback(() => {
    cancelThinkingFlush();
    pendingThinkingRef.current = '';
    stepsRef.current = [];
    const phase: ActivityPhase = {
      id: makeId('phase'),
      collapsed: false,
      steps: [{
        id: makeId('thinking'),
        kind: 'thinking',
        label: 'Analyzing…',
        body: '',
        status: 'active',
        expanded: true,
      }],
    };
    syncActivity([phase]);
    // Clear prior-run linkage so a new orchestration round does not anchor CoT
    // to an old assistant message while status is planning/working.
    setState((s) => ({
      ...s,
      status: 'planning',
      steps: [],
      answer: undefined,
      answerMessageId: undefined,
      partialCompletion: false,
      understanding: undefined,
    }));
  }, [cancelThinkingFlush, syncActivity]);

  const onPlanReady = useCallback((plan: ChefPlan) => {
    drainPendingThinkingChunks();
    settleActiveActivity('success');
    const steps: CoTStepData[] = (plan.steps ?? []).map((s, i) => ({
      title: s.description || s.toolName,
      status: i === 0 ? 'active' : 'pending',
    }));
    stepsRef.current = steps;
    setState((s) => ({
      ...s,
      status: 'working',
      steps,
      understanding: plan.understanding || undefined,
    }));
  }, [drainPendingThinkingChunks, settleActiveActivity]);

  const onThinkingChunk = useCallback((chunk: string) => {
    if (!chunk) return;
    pendingThinkingRef.current += chunk;
    if (thinkingRafRef.current != null) return;
    const scheduleFlush = () => {
      thinkingRafRef.current = requestAnimationFrame(() => {
        thinkingRafRef.current = null;
        const batch = pendingThinkingRef.current;
        pendingThinkingRef.current = '';
        if (batch) applyThinkingTextBatch(batch);
        if (pendingThinkingRef.current) scheduleFlush();
      });
    };
    scheduleFlush();
  }, [applyThinkingTextBatch]);

  const onStepStart = useCallback((stepIndex: number, description?: string, category?: StepCategory) => {
    drainPendingThinkingChunks();
    settleActiveActivity('success');
    ensurePhase();
    patchCurrentPhase((phase) => ({
      ...phase,
      collapsed: false,
      steps: [
        ...phase.steps,
        {
          id: makeId(`tool-${stepIndex}`),
          kind: 'tool_call',
          icon: activityIconForCategory(category),
          label: description || `Run step ${stepIndex + 1}`,
          status: 'active',
        },
      ],
    }));

    const prev = stepsRef.current;

    // Dynamic executor fallback: step_start fires without plan_ready
    if (prev.length === 0 || stepIndex >= prev.length) {
      // Grow steps array to accommodate
      const next = [...prev];
      while (next.length <= stepIndex) {
        next.push({
          title: `Step ${next.length + 1}`,
          status: 'pending',
        });
      }
      next[stepIndex] = {
        ...next[stepIndex],
        title: description || next[stepIndex].title,
        status: 'active',
        startedAt: Date.now(),
        description: undefined,
        category,
      };
      // Mark all earlier steps as done
      for (let i = 0; i < stepIndex; i++) {
        if (next[i].status !== 'done' && next[i].status !== 'error') {
          next[i] = { ...next[i], status: 'done' };
        }
      }
      stepsRef.current = next;
      setState((s) => ({ ...s, status: 'working', steps: next }));
      return;
    }

    const next = prev.map((step, i) => {
      if (i === stepIndex) {
        return {
          ...step,
          status: 'active' as const,
          startedAt: Date.now(),
          description: description || step.description,
          category: category || step.category,
        };
      }
      // Mark previous steps as done if they weren't already
      if (i < stepIndex && step.status !== 'done' && step.status !== 'error') {
        return { ...step, status: 'done' as const };
      }
      return step;
    });
    stepsRef.current = next;
    setState((s) => ({ ...s, status: 'working', steps: next }));
  }, [drainPendingThinkingChunks, ensurePhase, patchCurrentPhase, settleActiveActivity]);

  const onStepComplete = useCallback((stepIndex: number, resultSummary?: string) => {
    drainPendingThinkingChunks();
    settleActiveActivity('success');
    if (resultSummary) {
      ensurePhase();
      patchCurrentPhase((phase) => ({
        ...phase,
        steps: [
          ...phase.steps,
          {
            id: makeId(`result-${stepIndex}`),
            kind: 'tool_result',
            label: resultSummary,
            status: 'success',
            icon: 'check',
          },
        ],
      }));
    }

    const prev = stepsRef.current;
    if (stepIndex >= prev.length) return;
    // Don't overwrite a 'confirm' step — wait for user action
    if (prev[stepIndex].status === 'confirm') return;

    const next = prev.map((step, i) =>
      i === stepIndex ? { ...step, status: 'done' as const } : step
    );
    stepsRef.current = next;
    setState((s) => ({ ...s, steps: next }));
  }, [drainPendingThinkingChunks, ensurePhase, patchCurrentPhase, settleActiveActivity]);

  const onStepEditBatch = useCallback((stepIndex: number, editBatch: EditBatch) => {
    drainPendingThinkingChunks();
    const prev = stepsRef.current;

    const batchHasPending = (b: EditBatch | undefined) =>
      Boolean(b?.edits.some((e) => e.status === 'pending' || e.status === 'superseded'));

    const byIdIdx = prev.findIndex((s) => s.editBatch?.id === editBatch.id);
    if (byIdIdx >= 0) {
      const next = prev.map((step, i) =>
        i === byIdIdx
          ? {
              ...step,
              title: step.title || 'Review proposed edits',
              status: 'done' as const,
              description: `${editBatch.edits.length} proposed edit${editBatch.edits.length === 1 ? '' : 's'}`,
              category: 'edit' as const,
              editBatch,
            }
          : step,
      );
      stepsRef.current = next;
      setState((s) => ({ ...s, status: 'working', steps: next }));
      return;
    }

    let targetIndex = Math.max(0, stepIndex);
    const occupant = prev[targetIndex];
    if (
      occupant?.editBatch
      && occupant.editBatch.id !== editBatch.id
      && batchHasPending(occupant.editBatch)
    ) {
      // A different staged review is still pending on this row — add a dedicated row
      // so every batch stays reviewable (multi-edit / snippet follow-ups).
      targetIndex = prev.length;
    }

    const next = [...prev];
    while (next.length <= targetIndex) {
      next.push({ title: `Step ${next.length + 1}`, status: 'pending' });
    }
    next[targetIndex] = {
      ...next[targetIndex],
      title: next[targetIndex].title || 'Review proposed edits',
      status: 'done',
      description: `${editBatch.edits.length} proposed edit${editBatch.edits.length === 1 ? '' : 's'}`,
      category: 'edit',
      editBatch,
    };
    stepsRef.current = next;
    setState((s) => ({ ...s, status: 'working', steps: next }));
  }, [drainPendingThinkingChunks]);

  const updateEditBatch = useCallback((editBatch: EditBatch) => {
    const prev = stepsRef.current;
    const next = prev.map((step) =>
      step.editBatch?.id === editBatch.id ? { ...step, editBatch } : step
    );
    stepsRef.current = next;
    setState((s) => ({ ...s, steps: next }));
  }, []);

  const onStepError = useCallback((stepIndex: number, error: string, errorCode?: CoTErrorCode, toolName?: string, retryable?: boolean) => {
    drainPendingThinkingChunks();
    settleActiveActivity('error');
    ensurePhase();
    patchCurrentPhase((phase) => ({
      ...phase,
      steps: [
        ...phase.steps,
        {
          id: makeId(`error-${stepIndex}`),
          kind: 'tool_result',
          label: error || 'Step failed',
          status: 'error',
          icon: 'flag',
        },
      ],
    }));

    const prev = stepsRef.current;
    if (stepIndex >= prev.length) return;

    failureCountRef.current += 1;
    const next = prev.map((step, i) =>
      i === stepIndex
        ? { ...step, status: 'error' as const, errorMessage: error, errorCode, errorToolName: toolName, retryable }
        : step
    );
    stepsRef.current = next;
    setState((s) => ({ ...s, status: 'error', steps: next, failureCount: failureCountRef.current }));
  }, [drainPendingThinkingChunks, ensurePhase, patchCurrentPhase, settleActiveActivity]);

  const onDone = useCallback((answer?: CoTAnswer, partialCompletion?: boolean) => {
    drainPendingThinkingChunks();
    settleActiveActivity('success');
    // Preserve step-level error state — don't overwrite 'error' steps to 'done'.
    // This lets the UI show which steps failed even when the run produced a result.
    const next = stepsRef.current.map((step) => ({
      ...step,
      status: step.status === 'error' ? ('error' as const) : ('done' as const),
    }));
    stepsRef.current = next;
    setState((s) => ({ ...s, status: 'done', steps: next, answer, partialCompletion: partialCompletion ?? false }));
  }, [drainPendingThinkingChunks, settleActiveActivity]);

  const onError = useCallback((_message: string) => {
    drainPendingThinkingChunks();
    settleActiveActivity('error');
    failureCountRef.current += 1;
    setState((s) => ({ ...s, status: 'error', failureCount: failureCountRef.current }));
  }, [drainPendingThinkingChunks, settleActiveActivity]);

  const setAnswerMessageId = useCallback((id: string) => {
    setState((s) => ({ ...s, answerMessageId: id }));
  }, []);

  const onStepConfirmNeeded = useCallback((
    stepIndex: number,
    toolName: string,
    description: string,
    permissionTier: PermissionTier,
  ) => {
    drainPendingThinkingChunks();
    const prev = stepsRef.current;
    const next = [...prev];
    // Grow if needed (same pattern as onStepStart)
    while (next.length <= stepIndex) {
      next.push({ title: `Step ${next.length + 1}`, status: 'pending' });
    }
    // Mark earlier non-settled steps as done
    for (let i = 0; i < stepIndex; i++) {
      if (next[i].status !== 'done' && next[i].status !== 'error') {
        next[i] = { ...next[i], status: 'done' };
      }
    }
    next[stepIndex] = {
      ...next[stepIndex],
      status: 'confirm',
      confirmToolName: toolName,
      confirmDescription: description,
      confirmPermissionTier: permissionTier,
    };
    stepsRef.current = next;
    setState((s) => ({ ...s, status: 'working', steps: next }));
  }, [drainPendingThinkingChunks]);

  const onStepConfirmed = useCallback((stepIndex: number) => {
    const prev = stepsRef.current;
    if (stepIndex >= prev.length) return;
    const next = prev.map((step, i) =>
      i === stepIndex
        ? {
            ...step,
            status: 'active' as const,
            confirmToolName: undefined,
            confirmDescription: undefined,
            confirmPermissionTier: undefined,
          }
        : step
    );
    stepsRef.current = next;
    setState((s) => ({ ...s, steps: next }));
  }, []);

  const onClarificationStep = useCallback((
    stepIndex: number,
    question: string,
    options?: string[],
    allowFreeInput?: boolean,
  ) => {
    drainPendingThinkingChunks();
    settleActiveActivity('success');
    ensurePhase();
    patchCurrentPhase((phase) => ({
      ...phase,
      steps: [
        ...phase.steps,
        {
          id: makeId(`clarify-${stepIndex}`),
          kind: 'clarification',
          question,
          options,
          status: 'active',
        },
      ],
    }));

    const prev = stepsRef.current;
    const next = [...prev];
    while (next.length <= stepIndex) {
      next.push({ title: `Step ${next.length + 1}`, status: 'pending' });
    }
    for (let i = 0; i < stepIndex; i++) {
      if (next[i].status !== 'done' && next[i].status !== 'error') {
        next[i] = { ...next[i], status: 'done' };
      }
    }
    next[stepIndex] = {
      ...next[stepIndex],
      status: 'clarify',
      title: 'Needs clarification',
      category: 'clarify',
      clarificationQuestion: question,
      clarificationOptions: options,
      clarificationAllowFreeInput: allowFreeInput ?? true,
    };
    stepsRef.current = next;
    setState((s) => ({ ...s, status: 'working', steps: next }));
  }, [drainPendingThinkingChunks, ensurePhase, patchCurrentPhase, settleActiveActivity]);

  const onClarificationAnswered = useCallback((stepIndex: number) => {
    const prev = stepsRef.current;
    if (stepIndex >= prev.length) return;
    const next = prev.map((step, i) =>
      i === stepIndex
        ? {
            ...step,
            status: 'done' as const,
            title: 'Clarification resolved',
            clarificationQuestion: undefined,
            clarificationOptions: undefined,
            clarificationAllowFreeInput: undefined,
          }
        : step
    );
    stepsRef.current = next;
    setState((s) => ({ ...s, steps: next }));
  }, []);

  const reset = useCallback(() => {
    cancelThinkingFlush();
    pendingThinkingRef.current = '';
    stepsRef.current = [];
    activityRef.current = [];
    failureCountRef.current = 0;
    setState(IDLE_STATE);
  }, [cancelThinkingFlush]);

  /**
   * Snapshot-driven setter for callers that already hold the full desired
   * state (e.g. the V2 query-runs path projects AgentRunSnapshot.tasks
   * into CoTStepData[] and calls this on every snapshot change). Bypasses
   * the lifecycle callbacks — meant for reactive bridges, not per-event
   * orchestration.
   */
  const applySnapshot = useCallback((next: {
    status: CoTStatus;
    steps: CoTStepData[];
    answer?: CoTAnswer;
    answerMessageId?: string;
    partialCompletion?: boolean;
    understanding?: string;
    activityPhases?: ActivityPhase[];
  }) => {
    stepsRef.current = next.steps;
    const terminalActivityStatus = next.status === 'done'
      ? 'success'
      : next.status === 'error'
        ? 'error'
        : null;
    const nextActivityPhases = next.activityPhases
      ?? (terminalActivityStatus
        ? activityRef.current.map((phase) => ({
            ...phase,
            steps: phase.steps.map((step) =>
              step.status === 'active' ? { ...step, status: terminalActivityStatus } : step,
            ),
          }))
        : undefined);
    if (nextActivityPhases) activityRef.current = nextActivityPhases;
    setState((s) => ({
      ...s,
      status: next.status,
      steps: next.steps,
      answer: next.answer ?? s.answer,
      answerMessageId: next.answerMessageId ?? s.answerMessageId,
      partialCompletion: next.partialCompletion ?? s.partialCompletion,
      understanding: next.understanding ?? s.understanding,
      activityPhases: nextActivityPhases ?? s.activityPhases,
    }));
  }, []);

  const toggleActivityPhase = useCallback((phaseId: string) => {
    const next = activityRef.current.map((phase) =>
      phase.id === phaseId ? { ...phase, collapsed: !phase.collapsed } : phase
    );
    syncActivity(next);
  }, [syncActivity]);

  const toggleActivityStep = useCallback((phaseId: string, stepId: string) => {
    const next = activityRef.current.map((phase) => {
      if (phase.id !== phaseId) return phase;
      return {
        ...phase,
        steps: phase.steps.map((step): ActivityStep => {
          if (step.id !== stepId || step.kind !== 'thinking') return step;
          return { ...step, expanded: !step.expanded };
        }),
      };
    });
    syncActivity(next);
  }, [syncActivity]);

  return {
    state,
    onPlanning,
    onPlanReady,
    onThinkingChunk,
    onStepStart,
    onStepComplete,
    onStepError,
    onStepEditBatch,
    updateEditBatch,
    toggleActivityPhase,
    toggleActivityStep,
    onDone,
    onError,
    onStepConfirmNeeded,
    onStepConfirmed,
    onClarificationStep,
    onClarificationAnswered,
    setAnswerMessageId,
    applySnapshot,
    reset,
  };
}
